import { enableField, disableField, setFieldValue } from '../../../actions';
import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationCardState } from '../../../reducers';
import { LongRangeFieldIds } from '../base-config-models';
import { GAIN_CONTROL_AUTO_NUMERIC, GainControlValue, MonitorSeriesNames } from 'app/shared/components/location-card/location-card.constants';
import { field } from '../../../core/field-schema';
import { required, pattern } from '../../../core/validator-builders';
import { ComportOption, SampleRateElement } from 'app/shared/components/location-card/models/location-card.model';
import { EchoLocationCardState, EchoMonitorConfiguration } from '../models';


/**
 * Echo Long Range Depth Configuration Section
 */
export const echoLongRange = (state: EchoLocationCardState): Section => {
    const details = state.locationDetails && state.locationDetails.length > 0 ? state.locationDetails[0] : null;
    const settings = state.settings;
    const config = state.monitorConfiguration;

    const isSwitchMonitor = details && details.series !== MonitorSeriesNames.echo;
    const isMetric = settings.isMetric;
    return {
        info: {
            id: 'echo-long-range-section',
            title: 'ADD_EDIT.ADD_EDIT_LONG_RANGE',
            displayTitle: true,
            expandable: true,
            expanded: false,
            panelClass: 'surface-combo-section',
        },
        rows: [
            // Manhole depth Row
            {
                info: {
                    id: 'manhole-depth-row',
                    panelClass: 'custom-width',
                },
                items: [
                    field.number(LongRangeFieldIds.ManholeDepth, 'manholedepth', {
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.MANHOLE_DEPTH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'full-width',
                        value: details ? details.manholeDepth : null,
                        readonly: true,
                    }),
                ],
            },

            // Physical Offset and Store Data Row
            {
                info: {
                    id: 'physical-offset-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(LongRangeFieldIds.PhysicalOffset, 'physicalOffset', {
                        placeholder: 'ADD_EDIT.PHYSICAL_OFFSET',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor ? Number(config.physicaloffset.toFixed(2)) : isMetric ? 355.6 : 14,
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                    }),
                    field.toggle(LongRangeFieldIds.StoreData, 'storeData', {
                        label: 'ADD_EDIT.STORE_DATA',
                        panelClass: 'each-Item full-width item-flex-center',
                        value: config && !isSwitchMonitor ? config.storeData : true,
                    }),
                ],
            },

            // Blanking and Temperature Sensor Row
            {
                info: {
                    id: 'blanking-temperature-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(LongRangeFieldIds.Blanking, 'blanking', {
                        placeholder: 'ADD_EDIT.BLANKING',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor ? config.blanking : 0,
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                    }),
                    field.select<SampleRateElement, number>(LongRangeFieldIds.TemperatureSensor, 'temperatureSensor', {
                        placeholder: 'LOCATION_DASHBOARD.TEMPERATURE_SCALE_AXIS',
                        panelClass: 'each-Item full-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        value: config && !isSwitchMonitor ? config.temperatureSensor : 0,
                        optionsSelector: SelectorIdentifier.SensorTemp,
                    }),
                ],
            },

            // Power and Electronic Offset Row
            {
                info: {
                    id: 'power-electronic-offset-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<SampleRateElement, number>(LongRangeFieldIds.Power, 'power', {
                        placeholder: 'ADD_EDIT.POWER',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor ? config.power : 0,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.EchoSensorPower,
                    }),
                    field.number(LongRangeFieldIds.ElectronicOffset, 'electronicOffset', {
                        placeholder: 'ADD_EDIT.ELECTRONIC_OFFSET',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor ? config.electronicOffset : 0,
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                    }),
                ],
            },

            // Gain Control Row
            {
                info: {
                    id: 'gain-control-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<ComportOption, string>(LongRangeFieldIds.GainControlSelection, 'gainControlSelection', {
                        placeholder: 'ADD_EDIT.GAIN_CONTROL',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor && config.gainControl !== GAIN_CONTROL_AUTO_NUMERIC ? GainControlValue.Manual : GainControlValue.Auto,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.GainControlOptions,
                        events: {
                            onChange: (value) => [
                                value.value === GainControlValue.Manual
                                    ? enableField({ fieldId: LongRangeFieldIds.GainControl })
                                    : disableField({ fieldId: LongRangeFieldIds.GainControl }),
                                ...(value.value === GainControlValue.Auto
                                    ? [setFieldValue({ fieldId: LongRangeFieldIds.GainControl, value: GAIN_CONTROL_AUTO_NUMERIC })]
                                    : []),
                            ],
                        },
                    }),
                    field.select<SampleRateElement, number>(LongRangeFieldIds.GainControl, 'gainControl', {
                        placeholder: 'ADD_EDIT.GAIN_VALUE',
                        panelClass: 'each-Item full-width',
                        value: config && !isSwitchMonitor ? config.gainControl : GAIN_CONTROL_AUTO_NUMERIC,
                        disabled: !(config && !isSwitchMonitor && config.gainControl !== GAIN_CONTROL_AUTO_NUMERIC),
                        optionsSelector: SelectorIdentifier.GainValues,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Toggle Switches Row
            {
                info: {
                    id: 'toggle-switches-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(LongRangeFieldIds.UnidepthFiltering, 'unidepthFiltering', {
                        label: 'ADD_EDIT.UNIDEPTH_FILTERING',
                        panelClass: 'each-Item full-width item-flex-center',
                        value: config && !isSwitchMonitor ? config.unidepthFiltering : true,
                    }),
                    field.toggle(LongRangeFieldIds.PressureStore, 'pressureStore', {
                        label: 'ADD_EDIT.STORE_PRESSURE',
                        panelClass: 'each-Item full-width item-flex-center',
                        value: config && !isSwitchMonitor ? config.pressureStore : true,
                    }),
                ],
            },

            // Compensated Temperature Subtitle
            {
                info: {
                    id: 'compensated-temperature-subtitle-row',
                },
                items: [
                    {
                        id: 'compensated-temperature-subtitle',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        label: 'ADD_EDIT.COMPENSATED_TEMPERATURE',
                        panelClass: 'input-text subtitle-text',
                    },
                ],
            },

            // Compensated Temperature Row
            {
                info: {
                    id: 'compensated-temperature-row',
                    panelClass: 'custom-50p text-space',
                },
                items: [
                    field.number(LongRangeFieldIds.CompensatedTemperatureMarch, 'compensatedTemperatureMarch', {
                        placeholder: 'ADD_EDIT.MARCH',
                        panelClass: 'each-Item full-width',
                        value:
                            config && !isSwitchMonitor
                                ? Number(config.compensatedTemperatureMarch.toFixed(2))
                                : isMetric
                                ? 12.78
                                : 55,
                        placeholderSuffix: settings.tempUnit,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal2PlacePattern
                                    : REGEX_CONFIG.numericDecimal1PlacePattern,
                                message: 'COMMON.TEMPERATURE_ERROR',
                            }),
                        ],
                    }),
                    field.number(LongRangeFieldIds.CompensatedTemperatureSeptember, 'compensatedTemperatureSeptember', {
                        placeholder: 'ADD_EDIT.SEPTEMBER',
                        panelClass: 'each-Item full-width',
                        value:
                            config && !isSwitchMonitor
                                ? Number(config.compensatedTemperatureSeptember.toFixed(2))
                                : isMetric
                                ? 22.78
                                : 73,
                        placeholderSuffix: settings.tempUnit,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal2PlacePattern
                                    : REGEX_CONFIG.numericDecimal1PlacePattern,
                                message: 'COMMON.TEMPERATURE_ERROR',
                            }),
                        ],
                    }),
                ],
            },
        ],
    };
};
